/**
 * @fileoverview Inventory frontend service
 *
 * Typed API client for the /core/organization/inventory endpoints.
 * Mirrors the helper pattern used in work-order-service.tsx.
 *
 * @author FacilityAI
 * @version 1.0.0
 */

import { CORE_URL } from "@/constants/api";
import { VITE_CORE_API_URL } from "@/constants/env";
import { auth } from "@/services/auth-service";

const BASE = `${VITE_CORE_API_URL}${CORE_URL}/organization/inventory`;
const USER_BASE = `${VITE_CORE_API_URL}${CORE_URL}/user`;

// ─── helpers ────────────────────────────────────────────────────────────────

async function getToken(): Promise<string> {
	const token = await auth?.currentUser?.getIdToken();
	if (!token) throw new Error("Authentication required");
	return token;
}

async function request<T>(
	url: string,
	method: "GET" | "POST" | "PUT" | "DELETE",
	body?: unknown,
): Promise<T> {
	const token = await getToken();
	const init: RequestInit = {
		method,
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
	};
	if (body !== undefined) {
		init.body = JSON.stringify(body);
	}
	const res = await fetch(url, init);
	const json = await res.json().catch(() => ({}) as Record<string, unknown>);
	if (!res.ok || !json?.success) {
		const msg =
			(json && typeof json === "object" && "message" in json
				? String((json as Record<string, unknown>).message)
				: null) ?? "Request failed";
		throw new Error(msg);
	}
	return (json as { data: T }).data;
}

// ─── Types ──────────────────────────────────────────────────────────────────

export type InventoryTransactionType =
	| "RECEIVE"
	| "USE"
	| "ADJUST"
	| "TRANSFER_IN"
	| "TRANSFER_OUT";

export type InventoryAlertStatus = "OPEN" | "ACKNOWLEDGED" | "RESOLVED";

export interface InventoryItem {
	id: number;
	organizationId: number;
	name: string;
	description: string | null;
	sku: string | null;
	unit: string;
	unitCost: number | null;
	category: string | null;
	preferredVendorId: number | null;
	compatibleAssetTypeIds: number[];
	defaultReorderThreshold: number | null;
	defaultReorderQuantity: number | null;
	isDeleted: boolean;
	createdAt: string;
	updatedAt: string;
	preferredVendorName?: string | null;
	preferredVendorEmail?: string | null;
	preferredVendorPhone?: string | null;
	totalQuantityOnHand?: number;
	locationsStocked?: number;
	openAlerts?: number;
}

export interface InventoryStockRow {
	id: number;
	inventoryItemId: number;
	locationId: number;
	quantityOnHand: number;
	reorderThreshold: number | null;
	reorderQuantity: number | null;
	lastAlertedAt: string | null;
	createdAt: string;
	updatedAt: string;
	itemName: string;
	itemSku: string | null;
	itemUnit: string;
	itemCategory: string | null;
	preferredVendorId: number | null;
	preferredVendorName: string | null;
	locationName: string;
	effectiveThreshold: number | null;
	isLowStock: boolean;
}

export interface InventoryStockSummaryRow {
	itemId: number;
	itemName: string;
	itemSku: string | null;
	itemUnit: string;
	itemCategory: string | null;
	totalQuantity: number;
	locationsStocked: number;
	lowStockLocations: number;
}

export interface InventoryTransactionRow {
	id: number;
	inventoryItemId: number;
	locationId: number;
	transactionType: InventoryTransactionType;
	quantityDelta: number;
	quantityAfter: number;
	assetId: number | null;
	workOrderId: number | null;
	relatedTransactionId: number | null;
	notes: string | null;
	performedByUserId: number | null;
	createdAt: string;
	itemName: string;
	itemUnit: string;
	locationName: string;
	performedByUserName: string | null;
	assetTag: string | null;
}

export interface InventoryLowStockAlertRow {
	id: number;
	organizationId: number;
	inventoryItemId: number;
	locationId: number;
	quantityAtAlert: number;
	thresholdAtAlert: number;
	status: InventoryAlertStatus;
	acknowledgedByUserId: number | null;
	acknowledgedAt: string | null;
	resolvedAt: string | null;
	createdAt: string;
	itemName: string;
	itemUnit: string;
	locationName: string;
	preferredVendorName: string | null;
	preferredVendorEmail: string | null;
	preferredVendorPhone: string | null;
	acknowledgedByUserName: string | null;
}

export interface InventoryNotificationRecipientRow {
	id: number;
	organizationId: number;
	locationId: number | null;
	userId: number;
	createdAt: string;
	userName: string;
	userEmail: string | null;
	locationName: string | null;
}

export interface InventoryStats {
	totalItems: number;
	totalStockRows: number;
	totalLowStockRows: number;
	openAlerts: number;
	totalQuantityOnHand: number;
	estimatedInventoryValue: number;
}

export interface RecordTransactionInput {
	itemId: number;
	locationId: number;
	transactionType: InventoryTransactionType;
	quantityDelta: number;
	assetId?: number | null;
	workOrderId?: number | null;
	notes?: string;
}

export interface RecordTransactionResult {
	transaction: InventoryTransactionRow;
	stock: InventoryStockRow;
	alertCreated: boolean;
	alertId: number | null;
}

export interface CreateItemInput {
	name: string;
	description?: string;
	sku?: string;
	unit?: string;
	unitCost?: number;
	category?: string;
	preferredVendorId?: number | null;
	compatibleAssetTypeIds?: number[];
	defaultReorderThreshold?: number | null;
	defaultReorderQuantity?: number | null;
}

export type UpdateItemInput = Partial<CreateItemInput>;

// ─── Stats ──────────────────────────────────────────────────────────────────

export async function getInventoryStats(): Promise<InventoryStats> {
	return request<InventoryStats>(`${BASE}/stats`, "GET");
}

// ─── Items ──────────────────────────────────────────────────────────────────

export interface ItemsListResponse {
	items: InventoryItem[];
	total: number;
	page: number;
	limit: number;
}

export async function listInventoryItems(
	params: Record<string, string | number | boolean> = {},
): Promise<ItemsListResponse> {
	const qs = new URLSearchParams();
	for (const [k, v] of Object.entries(params)) {
		if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
	}
	const url = `${BASE}/items${qs.toString() ? `?${qs}` : ""}`;
	return request<ItemsListResponse>(url, "GET");
}

export async function getInventoryItem(id: number): Promise<InventoryItem> {
	return request<InventoryItem>(`${BASE}/items/${id}`, "GET");
}

export async function createInventoryItem(
	input: CreateItemInput,
): Promise<InventoryItem> {
	return request<InventoryItem>(`${BASE}/items`, "POST", input);
}

export async function updateInventoryItem(
	id: number,
	input: UpdateItemInput,
): Promise<InventoryItem> {
	return request<InventoryItem>(`${BASE}/items/${id}`, "PUT", input);
}

export async function deleteInventoryItem(id: number): Promise<void> {
	await request<{ id: number }>(`${BASE}/items/${id}`, "DELETE");
}

// ─── Stock ──────────────────────────────────────────────────────────────────

export interface StockListResponse {
	stock: InventoryStockRow[];
	total: number;
	page: number;
	limit: number;
}

export async function listInventoryStock(
	params: Record<string, string | number | boolean> = {},
): Promise<StockListResponse> {
	const qs = new URLSearchParams();
	for (const [k, v] of Object.entries(params)) {
		if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
	}
	const url = `${BASE}/stock${qs.toString() ? `?${qs}` : ""}`;
	return request<StockListResponse>(url, "GET");
}

export async function getInventoryStockSummary(): Promise<{
	summary: InventoryStockSummaryRow[];
}> {
	return request<{ summary: InventoryStockSummaryRow[] }>(
		`${BASE}/stock/summary`,
		"GET",
	);
}

export async function setInventoryStock(input: {
	itemId: number;
	locationId: number;
	quantityOnHand: number;
	reorderThreshold?: number | null;
	reorderQuantity?: number | null;
}): Promise<InventoryStockRow> {
	return request<InventoryStockRow>(`${BASE}/stock`, "POST", input);
}

// ─── Transactions ───────────────────────────────────────────────────────────

export interface TransactionsListResponse {
	transactions: InventoryTransactionRow[];
	total: number;
	page: number;
	limit: number;
}

export async function listInventoryTransactions(
	params: Record<string, string | number | boolean> = {},
): Promise<TransactionsListResponse> {
	const qs = new URLSearchParams();
	for (const [k, v] of Object.entries(params)) {
		if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
	}
	const url = `${BASE}/transactions${qs.toString() ? `?${qs}` : ""}`;
	return request<TransactionsListResponse>(url, "GET");
}

export async function recordInventoryTransaction(
	input: RecordTransactionInput,
): Promise<RecordTransactionResult> {
	return request<RecordTransactionResult>(
		`${BASE}/transactions`,
		"POST",
		input,
	);
}

// ─── Alerts ─────────────────────────────────────────────────────────────────

export interface AlertsListResponse {
	alerts: InventoryLowStockAlertRow[];
	total: number;
	page: number;
	limit: number;
}

export async function listInventoryAlerts(
	params: Record<string, string | number | boolean> = {},
): Promise<AlertsListResponse> {
	const qs = new URLSearchParams();
	for (const [k, v] of Object.entries(params)) {
		if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
	}
	const url = `${BASE}/alerts${qs.toString() ? `?${qs}` : ""}`;
	return request<AlertsListResponse>(url, "GET");
}

export async function acknowledgeInventoryAlert(
	id: number,
): Promise<InventoryLowStockAlertRow> {
	return request<InventoryLowStockAlertRow>(
		`${BASE}/alerts/${id}/acknowledge`,
		"POST",
		{},
	);
}

export async function resolveInventoryAlert(
	id: number,
): Promise<InventoryLowStockAlertRow> {
	return request<InventoryLowStockAlertRow>(
		`${BASE}/alerts/${id}/resolve`,
		"POST",
		{},
	);
}

// ─── Notification recipients ────────────────────────────────────────────────

export async function listInventoryNotificationRecipients(): Promise<{
	recipients: InventoryNotificationRecipientRow[];
}> {
	return request<{ recipients: InventoryNotificationRecipientRow[] }>(
		`${BASE}/notification-recipients`,
		"GET",
	);
}

export async function addInventoryNotificationRecipient(input: {
	userId: number;
	locationId?: number | null;
}): Promise<InventoryNotificationRecipientRow> {
	return request<InventoryNotificationRecipientRow>(
		`${BASE}/notification-recipients`,
		"POST",
		input,
	);
}

export async function removeInventoryNotificationRecipient(
	id: number,
): Promise<void> {
	await request<{ id: number }>(
		`${BASE}/notification-recipients/${id}`,
		"DELETE",
	);
}

// ─── User notifications (general — used by the navbar bell + this module) ───

export interface UserNotification {
	id: number;
	userId: number;
	message: string | null;
	type: string | null;
	isUnread: boolean;
	createdAt: string;
	data: Record<string, unknown>;
	isDeleted: boolean;
}

export async function listUserNotifications(
	unreadOnly = false,
	limit = 50,
): Promise<{ notifications: UserNotification[]; unreadCount: number }> {
	const qs = new URLSearchParams();
	if (unreadOnly) qs.set("unreadOnly", "true");
	if (limit) qs.set("limit", String(limit));
	const url = `${USER_BASE}/notifications${qs.toString() ? `?${qs}` : ""}`;
	return request<{ notifications: UserNotification[]; unreadCount: number }>(
		url,
		"GET",
	);
}

export async function markAllUserNotificationsRead(): Promise<{
	unreadCount: number;
}> {
	return request<{ unreadCount: number }>(
		`${USER_BASE}/notifications/read-all`,
		"POST",
		{},
	);
}

export async function deleteUserNotification(id: number): Promise<void> {
	await request<{ id: number }>(`${USER_BASE}/notifications/${id}`, "DELETE");
}
